<template lang="pug">
div(class='mb-10 lg:container')
  div(class='px-4 pt-8 sm:px-6')
    //- if fetch is loading, show spinner
    template(v-if='pending')
      SpinnerText(text='Loading' is-dark)

    //- After fetch is finished
    template(v-else)
      div(class='flex items-center gap-4 mb-8')
        h1(class='text-3xl font-bold leading-tight tracking-tight m-0')
          | My Account

        BaseButton(size='XSMALL' button-style='OUTLINE' @click='logout') Sign out

      p(class='text-xl font-bold mb-4') My Orders

      //- if fetch returned an error
      template(v-if='error')
        p Unable to find orders. Please try again later.

      //- if we have orders
      template(v-else-if='orders && orders.length > 0')
        div(class='-mx-4 overflow-hidden ring-1 ring-gray-light sm:-mx-6 md:mx-0 md:rounded-lg')
          table(class='min-w-full divide-y divide-gray-light')
            thead(class='bg-gray-lighter')
              tr
                th(scope='col' class='pl-4 pr-3 text-left text-sm font-bold py-4 sm:pl-6') Order
                th(scope='col' class='hidden px-3 text-left text-sm font-bold py-4 md:table-cell') Date
                th(scope='col' class='hidden px-3 text-left text-sm font-bold py-4 lg:table-cell') Description
                th(scope='col' class='hidden px-3 text-left text-sm font-bold py-4 sm:table-cell') Status
                th(scope='col' class='pr-4 pl-3 text-right text-sm font-bold py-4') Total
                //- Ghost column header for the arrow in the list
                th(scope='col')

            tbody(class='divide-y divide-gray-200 bg-white')
              template(v-for='(order, index) in orders' :key='order.id + index')
                AccountOrderItem(:item='order')

      //- if orders is empty
      template(v-else)
        p No previous orders
</template>
<script setup lang="ts">
const { isIntegrationEnabled } = useUtils()

// 404 if cognito is not enabled
if (!isIntegrationEnabled('cognito')) {
  throw createError({ statusCode: 404, statusMessage: 'Invalid URL', fatal: true })
}

const router = useRouter()
const { $cognito } = useNuxtApp()
const { translateTemplate } = useTemplateTranslator()
const { getApiUrl } = useUrls()

const { data, pending, error } = await useAsyncData(
  'accountOrders',
  () => {
    return getUserOrders()
  },
  {
    lazy: true,
    server: false,
  }
)

useMetaTags({
  title: translateTemplate('My Account [TITLE_SUFFIX]'),
})

const isLoggedIn = computed(() => {
  return isIntegrationEnabled('cognito') && $cognito?.isLoggedIn()
})

watch(isLoggedIn, async (newVal) => {
  if (!newVal) router.replace({ name: 'home' })
})

const orders = computed(() => {
  if (!data.value) return
  return data.value.orders
})

async function getUserOrders(): Promise<AccountData | undefined> {
  const token = await $cognito.getToken()

  // If we don't have a token the the user is not logged in.
  if (!token) {
    router.replace({ name: 'home' })
    return
  }

  const url = getApiUrl('orders')
  return $fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

function logout() {
  $cognito.logout()
}

interface AccountData {
  orders: Order[]
}
</script>
